import { forwardRef, MutableRefObject, useCallback, useEffect, useState } from 'react'
import client from 'api/client'
import { FormikValues } from 'formik'
import { useMemberProfile } from 'hooks/useMemberProfile'
import { showErrorMessageToast } from '../../common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { APIFieldError, FormRef } from '../Form/Form'
import { reportError } from 'utils/error'
import { ProfileHeader } from '../UserProfileCard/ProfileHeader'
import cx from 'classnames'
import { RippleAnimation } from '../UserProfileCard/RippleAnimation'
import SkeletonStyles from '../UserProfileCard/SkeletonProfileCard.module.scss'
import { ErrorProfileCard } from '../UserProfileCard/ErrorProfileCard'
import { WorkspaceMembershipProfileForm } from './WorkspaceMembershipProfileForm'
import Styles from './FormWithSubmit.module.scss'
import FormStyles from './WorkspaceMemberhsipProfileForm.module.scss'
import { useDispatch } from 'react-redux'
import { upsertMemberProfile } from 'redux/actions/workspace'
import { useAuth } from 'hooks/UseAuth'
import { authMerge } from 'redux/actions'

interface Props {
  workspaceId: number
  memberId: number
  submitButtonContainer: MutableRefObject<HTMLElement> | Element
  onComplete: () => void
}

export const FormWithSubmit = forwardRef<FormRef, Props>(
  ({ workspaceId, memberId, submitButtonContainer, onComplete }: Props, ref) => {
    const auth = useAuth()
    const { profile, error } = useMemberProfile(workspaceId, memberId)
    const [errors, setErrors] = useState<APIFieldError[]>([])
    const dispatch = useDispatch()

    useEffect(() => {
      if (error) {
        showErrorMessageToast('There was an issue loading the profile. Our team has been notified.')
      }
    }, [error])

    const handleSubmit = useCallback(
      async (values: FormikValues) => {
        setErrors([])
        const response = await client.updateWorkspaceMemberProfile(workspaceId, memberId, values)
        if (response.type === 'success') {
          const updatedProfile = response.data
          dispatch(upsertMemberProfile(workspaceId, updatedProfile))
          if (auth?.id === memberId) {
            const { full_name, first_name, last_name } = updatedProfile
            dispatch(authMerge({ auth: { full_name, first_name, last_name } }))
          }
          onComplete()
        } else {
          if (response.data?.errors) {
            setErrors(response.data.errors)
          } else {
            reportError(`Error updating profile ${memberId}`, {
              severity: 'error',
              metaData: { info: response.data, values },
            })
            showErrorMessageToast('There was an error updating the profile. Our team has been notified.')
          }
        }
      },
      [workspaceId, memberId, onComplete, dispatch, auth?.id],
    )

    return (
      <>
        {profile && (
          <WorkspaceMembershipProfileForm
            ref={ref}
            errors={errors}
            profile={profile}
            onSubmit={handleSubmit}
            submitButtonContainer={submitButtonContainer}
          />
        )}
        {!profile && !error && (
          <RippleAnimation>
            <ProfileHeader
              classNames={{
                profileImage: FormStyles.profileImage,
              }}
              coverImage={<div className={SkeletonStyles.placeholder} />}
              profileImage={<div className={cx(SkeletonStyles.placeholder, SkeletonStyles.dark)} />}
            />
            <div className={Styles.skeletonContent}>
              <div className={cx(SkeletonStyles.placeholder, SkeletonStyles.block, Styles.skeletonPlaceholderSmall)} />
              <div className={cx(SkeletonStyles.placeholder, SkeletonStyles.block, Styles.skeletonPlaceholder)} />
              <div className={cx(SkeletonStyles.placeholder, SkeletonStyles.block)} />
              <div className={cx(SkeletonStyles.placeholder, SkeletonStyles.block, Styles.skeletonPlaceholder)} />
              <div className={cx(SkeletonStyles.placeholder, SkeletonStyles.block, Styles.skeletonPlaceholderTiny)} />
              <div className={cx(SkeletonStyles.placeholder, SkeletonStyles.block, Styles.skeletonPlaceholder)} />
            </div>
          </RippleAnimation>
        )}
        {error && <ErrorProfileCard />}
      </>
    )
  },
)
